import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../core/auth/auth.service';
import { SupabaseService } from '../../services/supabase.service';
import { MeService } from '../../core/services/me.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { SnackbarService } from '../../core/services/snackbar.service';
import {
  readPendingBootstrap,
  clearPendingBootstrap,
} from '../../core/auth/pending-bootstrap.storage';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
})
export class LoginPage implements OnInit, OnDestroy {
  authMethod: 'supabase' | 'apikey' = 'supabase';
  email = '';
  password = '';
  apiKey = '';
  loading = false;
  success = false;
  error: string | null = null;
  userEmail: string | null = null;
  hasAccess = false;
  
  // Bootstrap state
  needsBootstrap = false;
  bootstrapLoading = false;
  bootstrapSuccess = false;
  orgName = '';
  projectName = '';
  bootstrapApiKey: string | null = null;
  hasSession = false; // Track if user has active Supabase session
  
  private sessionSub?: Subscription;

  constructor(
    private authService: AuthService,
    private supabase: SupabaseService,
    private http: HttpClient,
    private router: Router,
    readonly route: ActivatedRoute,
    private snackbarService: SnackbarService,
    private meService: MeService
  ) {}

  ngOnInit() {
    // Check if user is already logged in
    // Use debounce to prevent multiple rapid calls
    this.sessionSub = this.supabase.getSession().pipe(
      debounceTime(300),
      distinctUntilChanged((prev, curr) => {
        const prevToken = prev?.access_token || null;
        const currToken = curr?.access_token || null;
        return prevToken === currToken;
      })
    ).subscribe(session => {
      this.hasSession = !!session?.access_token;
      if (session?.access_token) {
        // Only check bootstrap if we have a valid token
        this.checkBootstrap();
      } else {
        // Clear bootstrap state when session is lost
        this.needsBootstrap = false;
        this.success = false;
      }
    });
  }

  ngOnDestroy() {
    this.sessionSub?.unsubscribe();
  }

  async loginSupabase() {
    if (!this.email || !this.password) {
      this.error = 'Please enter email and password';
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      const { error } = await this.supabase.signIn(this.email, this.password);
      
      if (error) {
        this.error = error.message || 'Failed to login';
        this.loading = false;
        return;
      }

      // Check if user needs to bootstrap org/project
      await this.checkBootstrap();
      this.loading = false;
    } catch (err: any) {
      this.error = err.message || 'Failed to login';
      this.loading = false;
    }
  }

  /** Pre-fill org from sign-up when email confirmation delayed POST /auth/bootstrap. */
  private applyPendingOrgPrefill(): void {
    const p = readPendingBootstrap();
    if (!p) return;
    if (!this.orgName.trim()) {
      this.orgName = p.orgName;
    }
    if (!this.projectName.trim() && p.projectName) {
      this.projectName = p.projectName;
    }
  }

  async checkBootstrap() {
    try {
      const token = await this.supabase.getAccessToken();
      if (!token) {
        return;
      }

      try {
        const me = await firstValueFrom(this.meService.getMe(true));
        if (me?.needs_bootstrap || !me?.org) {
          try {
            const { me: afterEnsure } = await firstValueFrom(this.meService.ensureOrgIfNeeded());
            if (afterEnsure?.org && !afterEnsure.needs_bootstrap) {
              this.success = true;
              this.userEmail = afterEnsure.account_email ?? afterEnsure.org.name;
              this.hasAccess = afterEnsure.has_access;
              this.needsBootstrap = false;
              this.autoRedirectIfReturnUrl();
              return;
            }
          } catch {
            // Fall through to manual bootstrap UI.
          }
          this.needsBootstrap = true;
          this.applyPendingOrgPrefill();
        } else if (me.org) {
          this.success = true;
          this.userEmail = me.account_email ?? me.org.name;
          this.hasAccess = me.has_access;
          this.needsBootstrap = false;
          this.autoRedirectIfReturnUrl();
        }
      } catch (err: any) {
        if (err.status === 404 && err.error?.needs_bootstrap) {
          this.needsBootstrap = true;
          this.applyPendingOrgPrefill();
        } else {
          this.error = err.error?.error || 'Failed to check organization status';
        }
      }
    } catch (err: any) {
      this.error = 'Failed to check organization status';
    }
  }

  private autoRedirectIfReturnUrl(): void {
    const raw = this.route.snapshot.queryParams['returnUrl'];
    if (typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//')) {
      void this.router.navigateByUrl(raw);
    }
  }

  async bootstrap() {
    this.bootstrapLoading = true;
    this.error = null;

    try {
      // Interceptor will automatically add Authorization header
      const response = await firstValueFrom(this.http.post<any>(
        `${environment.apiUrl}/auth/bootstrap`,
        {
          org_name: this.orgName.trim() || undefined,
          project_name: this.projectName.trim() || undefined
        }
      ));

      this.bootstrapSuccess = true;
      this.bootstrapApiKey = response.api_key;
      this.userEmail = response.org.name;

      // Drop any cached /auth/me result from before org existed (e.g. 404 needs_bootstrap).
      this.meService.clearCache();

      // Store API key for gateway usage
      if (response.api_key) {
        this.authService.setApiKey(response.api_key);
      }

      clearPendingBootstrap();
      this.bootstrapLoading = false;
    } catch (err: any) {
      this.error = err.error?.error || 'Failed to create organization';
      this.bootstrapLoading = false;
    }
  }

  loginApiKey() {
    if (!this.apiKey) {
      this.error = 'Please enter your API key';
      return;
    }

    this.loading = true;
    this.error = null;

    this.authService.login(this.apiKey).subscribe({
      next: (response) => {
        this.success = true;
        this.userEmail = response.org.name;
        this.hasAccess = response.has_access;
        this.loading = false;
        
        this.authService.getMe().subscribe({
          next: (me) => {
            this.userEmail = me.org?.name || this.userEmail;
          },
          error: () => {},
        });
      },
      error: (err) => {
        if (err.status === 401) {
          this.error = 'Invalid API key';
        } else if (err.status === 402) {
          this.error = 'No active Spectyra access for this key. Open Billing to choose a plan.';
        } else {
          this.error = err.error?.error || 'Failed to login';
        }
        this.loading = false;
      },
    });
  }

  copyApiKey() {
    if (this.bootstrapApiKey) {
      navigator.clipboard.writeText(this.bootstrapApiKey).then(() => {
        this.snackbarService.showSuccess('API key copied to clipboard!');
      });
    }
  }

  goToApp() {
    const raw = this.route.snapshot.queryParams['returnUrl'];
    const target =
      typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//') ? raw : '/overview';
    void this.router.navigateByUrl(target);
  }
}
